import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit, Trash2, Search, Upload, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { advertisementsAPI, BASE_URL } from '../services/api';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Advertisements = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedAd, setSelectedAd] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isActive: true,
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await advertisementsAPI.getAll(); 
      if (response.data.success) {
        setAdvertisements(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching advertisements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setModalType('add');
    setFormData({
      title: '',
      description: '',
      isActive: true,
      image: null
    });
    setImagePreview(null);
    setIsModalOpen(true);
  };

  const handleEdit = (ad) => {
    setModalType('edit');
    setSelectedAd(ad);
    setFormData({
      title: ad.title,
      description: ad.description || '',
      isActive: ad.isActive,
      image: null
    });
    setImagePreview(ad.imageUrl ? `${BASE_URL}{ad.imageUrl}` : null);
    // setImagePreview(ad.imageUrl || null);
    setIsModalOpen(true);
  };

  const handleView = (ad) => {
    setModalType('view');
    setSelectedAd(ad);
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (ad) => {
    try {
      const response = await advertisementsAPI.toggleStatus(ad._id);
    //   if (response.data.success) {
    //     toast.success(`Advertisement ${ad.isActive ? 'deactivated' : 'activated'} successfully`);
    //     fetchAdvertisements();
    //   }
    if (response.data.success) {
        if (ad.isActive) {
            toast.error("Advertisement deactivated successfully");
        } else {
            toast.success("Advertisement activated successfully");
        }
        fetchAdvertisements();
    }

    } catch (error) {
      console.error('Error toggling advertisement status:', error);
    }
  };

  const handleDelete = async (ad) => {
    if (window.confirm(`Are you sure you want to delete "${ad.title}"?`)) {
      try {
        const response = await advertisementsAPI.delete(ad._id);
        if (response.data.success) {
          toast.success('Advertisement deleted successfully');
          fetchAdvertisements();
        }
      } catch (error) {
        console.error('Error deleting advertisement:', error);
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, image: null });
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('isActive', formData.isActive);
      
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      let response;
      if (modalType === 'add') {
        response = await advertisementsAPI.create(submitData);
      } else if (modalType === 'edit') {
        response = await advertisementsAPI.update(selectedAd._id, submitData);
      }

      if (response.data.success) {
        toast.success(`Advertisement ${modalType === 'add' ? 'created' : 'updated'} successfully`);
        setIsModalOpen(false);
        fetchAdvertisements();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredAdvertisements = advertisements.filter(ad =>
    ad.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (isActive) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Advertisements</h1>
        <button onClick={handleAdd} className="bg-gradient-to-r from-blue-500 to-purple-600 btn-primary">
          <Plus className="h-5 w-5" />
          Add Advertisement
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search advertisements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Advertisements Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAdvertisements.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500">No advertisements found</p>
            </div>
          ) : (
            filteredAdvertisements.map((ad) => (
              <div key={ad._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {ad.imageUrl && (
                  <img
                    // src={`http://localhost:5900${ad.imageUrl}`}
                    src={`${BASE_URL}${ad.imageUrl}`}
                    // src={ad.imageUrl}
                    alt={ad.title}
                    className="w-full h-48 object-cover"
                  />
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ad.isActive)}`}>
                      {ad.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {ad.title}
                  </h3>
                  {ad.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {ad.description}
                    </p>
                  )}
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleView(ad)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded-full hover:bg-blue-50"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(ad)}
                        className="text-yellow-600 hover:text-yellow-900 p-2 rounded-full hover:bg-yellow-50"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(ad)}
                        className="text-red-600 hover:text-red-900 p-2 rounded-full hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleToggleStatus(ad)}
                      className={`p-2 rounded-full ${ad.isActive ? 'text-green-600 hover:text-green-900 hover:bg-green-50' : 'text-red-600 hover:text-red-900 hover:bg-red-50'}`}
                      title={ad.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {ad.isActive ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          modalType === 'add' ? 'Add Advertisement' :
          modalType === 'edit' ? 'Edit Advertisement' : 'View Advertisement'
        }
        size="lg"
      >
        {modalType === 'view' ? (
          <div className="space-y-6">
            {selectedAd?.imageUrl && (
              <img
                // src={`http://localhost:5900${selectedAd.imageUrl}`}
                src={`${BASE_URL}${selectedAd.imageUrl}`}
                // src={selectedAd.imageUrl}
                alt={selectedAd.title}
                className="w-full h-64 object-cover rounded-lg"
              />
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <p className="text-lg text-gray-900">{selectedAd?.title}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedAd?.isActive)}`}>
                {selectedAd?.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {selectedAd?.description && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <p className="text-gray-900">{selectedAd.description}</p>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="input-field"
                placeholder="Enter advertisement title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="textarea-field"
                rows="3"
                placeholder="Enter advertisement description"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span className="text-sm font-medium text-gray-700">Active</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image (Max 5MB)
              </label>
              <div className="space-y-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          Upload an image
                        </span>
                        <input
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="btn-secondary"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-gradient-to-r from-blue-500 to-purple-600 btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <LoadingSpinner size="sm" />
                    {modalType === 'add' ? 'Creating...' : 'Updating...'}
                  </>
                ) : (
                  modalType === 'add' ? 'Create Advertisement' : 'Update Advertisement'
                )}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Advertisements;

